import React, { useState, useRef } from 'react';
import { Upload, Sparkles, MapPin, CheckCircle2, RefreshCw, AlertTriangle } from 'lucide-react';
import apiClient from '../utils/apiClient';
import { useFacility } from '../context/FacilityContext';

const Pane = ({ title, children, icon: Icon }) => (
  <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden flex flex-col">
    <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-slate-500" />}
      <h2 className="text-[13px] font-semibold text-slate-700">{title}</h2>
    </div>
    <div className="p-3 flex-1 min-h-[300px]">{children}</div>
  </div>
);

const MapSetup = () => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?._id || currentFacility?.id;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [result, setResult] = useState(null); // {upload_url, satellite_image_url, parsed, proposed, source}

  const onPick = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !facilityId) return;
    setUploading(true);
    setError(null);
    setInfo(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await apiClient.post(
        `/api/facilities/${facilityId}/map-setup/parse`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const updateRow = (bIdx, rIdx, key, val) => {
    setResult((prev) => {
      const next = { ...prev, parsed: { ...prev.parsed } };
      const buildings = (prev.parsed?.buildings || []).map((b, i) => ({ ...b, rows: [...(b.rows || [])] }));
      const row = { ...buildings[bIdx].rows[rIdx], [key]: key === 'unit_count' ? (parseInt(val, 10) || 0) : val };
      buildings[bIdx].rows[rIdx] = row;
      next.parsed.buildings = buildings;
      // Recompute proposed unit-count totals on the fly (rough preview).
      const total = buildings.reduce(
        (acc, b) => acc + (b.rows || []).reduce((a, r) => a + (parseInt(r.unit_count, 10) || 0), 0), 0,
      );
      next.proposed = { ...prev.proposed, total_units: total };
      return next;
    });
  };

  const onApply = async () => {
    if (!result?.proposed) return;
    if (!window.confirm(`Create ${result.proposed.total_units} units across ${result.proposed.unit_types.length} unit types?`)) return;
    setApplying(true);
    setError(null);
    try {
      // Re-derive units from parsed (in case operator edited counts)
      const units = [];
      let y = 1;
      for (const b of result.parsed.buildings || []) {
        for (const r of b.rows || []) {
          const count = parseInt(r.unit_count, 10) || 0;
          for (let i = 0; i < count; i++) {
            units.push({
              unit_number: `${r.row_label || b.label}-${String(i + 1).padStart(2, '0')}`,
              unit_size_label: r.unit_size_label,
              building: b.label,
              row: r.row_label,
              x: i + 1,
              y,
            });
          }
          y += 1;
        }
      }
      const payload = {
        unit_types: result.proposed.unit_types,
        units,
        overwrite: false,
      };
      const r = await apiClient.post(`/api/facilities/${facilityId}/map-setup/apply`, payload);
      setInfo(`Created ${r.data.created_units} unit(s); ${r.data.skipped_units?.length || 0} skipped (already existed).`);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-violet-600" />
            Site map setup (Path A)
          </h1>
          <p className="text-[13px] text-slate-400 mt-0.5 max-w-3xl">
            Upload a photo of your hand-drawn or printed facility map. We parse it with a vision model, cross-check against the Google satellite tile, and propose a unit layout you can edit before applying.
          </p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onUpload} className="hidden" />
          <button
            onClick={onPick}
            disabled={uploading || !facilityId}
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg text-[13px] font-medium"
          >
            {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? 'Parsing...' : 'Upload map photo'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200/80 bg-rose-50/50 p-3 mb-4">
          <p className="text-[12px] text-rose-700 flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5" /> {error}</p>
        </div>
      )}
      {info && (
        <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/50 p-3 mb-4">
          <p className="text-[12px] text-emerald-700 flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5" /> {info}</p>
        </div>
      )}

      {result?.source === 'mock' && (
        <div className="rounded-xl border border-amber-200/80 bg-amber-50/50 p-3 mb-4">
          <p className="text-[12px] text-amber-700">
            Vision API key not set — using a deterministic mock parse. Set <code>ANTHROPIC_API_KEY</code> on the server to use real parsing.
          </p>
        </div>
      )}

      {result ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
            <Pane title="Hand-drawn upload" icon={Upload}>
              {result.upload_url ? (
                <img src={result.upload_url} alt="Uploaded facility map" className="w-full h-full object-contain rounded" />
              ) : (
                <p className="text-[13px] text-slate-400">No upload</p>
              )}
            </Pane>

            <Pane title="Google satellite (validation)" icon={MapPin}>
              {result.satellite_image_url ? (
                <img src={result.satellite_image_url} alt="Facility satellite tile" className="w-full h-full object-contain rounded" />
              ) : (
                <div className="text-[12px] text-slate-400">
                  <p>No satellite tile cached.</p>
                  <p className="mt-2">Set <code>GOOGLE_MAPS_API_KEY</code> and trigger geocode from Settings &gt; Facility.</p>
                </div>
              )}
            </Pane>

            <Pane title="Proposed layout" icon={Sparkles}>
              <div className="text-[12px] space-y-2">
                <div className="rounded bg-slate-50 p-2.5">
                  <span className="text-slate-400">Total units: </span>
                  <span className="font-semibold text-slate-900">{result.proposed?.total_units || 0}</span>
                  <span className="text-slate-400 ml-3">Types: </span>
                  <span className="font-semibold text-slate-900">{result.proposed?.unit_types?.length || 0}</span>
                </div>
                {(result.parsed?.buildings || []).map((b, bIdx) => (
                  <div key={bIdx} className="rounded border border-slate-200/60 p-2.5">
                    <div className="text-[12px] font-semibold text-slate-700 mb-1.5">{b.label}</div>
                    {(b.rows || []).map((r, rIdx) => (
                      <div key={rIdx} className="grid grid-cols-3 gap-1.5 mb-1 text-[11px]">
                        <input
                          value={r.row_label || ''}
                          onChange={(e) => updateRow(bIdx, rIdx, 'row_label', e.target.value)}
                          className="border border-slate-200 rounded px-1.5 py-1"
                          placeholder="Row"
                        />
                        <input
                          type="number"
                          value={r.unit_count || 0}
                          onChange={(e) => updateRow(bIdx, rIdx, 'unit_count', e.target.value)}
                          className="border border-slate-200 rounded px-1.5 py-1"
                        />
                        <input
                          value={r.unit_size_label || ''}
                          onChange={(e) => updateRow(bIdx, rIdx, 'unit_size_label', e.target.value)}
                          className="border border-slate-200 rounded px-1.5 py-1"
                          placeholder="Size"
                        />
                      </div>
                    ))}
                  </div>
                ))}
                {result.parsed?.notes && (
                  <p className="text-[11px] text-slate-500 italic mt-2">{result.parsed.notes}</p>
                )}
              </div>
            </Pane>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => setResult(null)}
              className="px-4 py-2 border border-slate-200 hover:border-slate-300 rounded text-[13px] text-slate-700"
            >
              Discard
            </button>
            <button
              onClick={onApply}
              disabled={applying || !result.proposed?.total_units}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-[13px] font-medium"
            >
              {applying ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Apply layout ({result.proposed?.total_units} units)
            </button>
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <Upload className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-[13px] text-slate-500 mb-1">Drop a hand-drawn map photo, printed map, or CAD screenshot.</p>
          <p className="text-[12px] text-slate-400">Sketchy is fine — operator reviews and edits every row before applying.</p>
        </div>
      )}
    </div>
  );
};

export default MapSetup;
